<script setup lang="ts">
import { ref } from "vue";
import type { InputUploadProps } from "./inputUpload.types";
import { useInputUpload } from "./useInputUpload";

defineOptions({ name: "EInputUpload", inheritAttrs: false });

const props = withDefaults(defineProps<InputUploadProps>(), {
  color: "neutral",
  variant: "subtle",
  size: "md",
  block: false,
  button: false,
  multiple: false,
  disabled: false,
  required: false,
  icon: "upload",
  description: "Drag and drop files here or click to browse",
  preview: false,
});

const fileInputRef = ref<HTMLInputElement | null>(null);

const {
  classes,
  finalColor,
  finalVariant,
  finalSize,
  displayText,
  hasFiles,
  isButton,
  isDragActive,
  fileList,
  invalidFiles,
  openFilePicker,
  handleFileChange,
  handleDragEnter,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  removeFile,
  removeInvalidFile,
  formatFileSize,
  getFileIcon,
  invalidFileCardCls,
  fileCardCls,
  getPreviewUrl,
} = useInputUpload({ props, fileInputRef });
</script>

<template>
  <div part="base">
    <!-- Button mode: delegate to e-button -->
    <e-button
      v-if="isButton"
      exportparts="base:button,leading:button-leading,label:button-label,trailing:button-trailing,leading-icon:button-leading-icon,trailing-icon:button-trailing-icon"
      :icon="icon"
      :label="displayText"
      :color="finalColor"
      :variant="finalVariant"
      :size="finalSize"
      :disabled="disabled"
      :block="block"
      @click="openFilePicker"
      @keydown.enter.prevent="openFilePicker"
      @keydown.space.prevent="openFilePicker"
    />

    <!-- Drop zone mode -->
    <div
      v-else
      part="dropzone"
      :class="classes.base"
      class="e-input-upload"
      :data-disabled="disabled"
      :data-drag-active="isDragActive || undefined"
      :aria-disabled="disabled || undefined"
      role="button"
      :tabindex="disabled ? -1 : 0"
      @click="openFilePicker"
      @keydown.enter.prevent="openFilePicker"
      @keydown.space.prevent="openFilePicker"
      @dragenter="handleDragEnter"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <div part="dropzone-content" :class="classes.dropzoneContent" class="e-input-upload-dropzone-content">
        <slot>
          <span :class="classes.dropzoneIcon" class="e-input-upload-dropzone-icon">
            <e-icon
              v-show="isDragActive"
              exportparts="base:dropzone-icon-active"
              name="generate"
              size="xl"
              :class="classes.dropzoneIconActive"
            />
            <e-icon v-show="!isDragActive" exportparts="base:dropzone-icon" :name="icon" size="xl" />
          </span>
          <span :class="[classes.text, !hasFiles ? classes.placeholder : '']" class="e-input-upload-text">
            {{ displayText }}
          </span>
          <span :class="classes.dropzoneDescription" class="e-input-upload-dropzone-description">
            <template v-if="invalidFiles.length > 0">
              {{
                invalidFiles.length === 1 ? "Invalid file type" : `${invalidFiles.length} files have an invalid type`
              }}
            </template>
            <template v-else>{{ description }}</template>
          </span>
        </slot>
      </div>
    </div>

    <!-- Shared hidden file input -->
    <input
      ref="fileInputRef"
      :class="classes.fileInput"
      type="file"
      :name="name"
      :accept="accept"
      :multiple="multiple"
      :disabled="disabled"
      :required="required"
      tabindex="-1"
      aria-hidden="true"
      @change="handleFileChange"
    />

    <!-- Invalid file cards -->
    <div v-if="preview && invalidFiles.length > 0" :class="classes.fileList">
      <e-primitive v-for="(file, i) in invalidFiles" :key="`invalid-${i}`" :inner-class="invalidFileCardCls">
        <div :class="classes.fileCard">
          <span :class="classes.fileIcon">
            <e-icon :name="getFileIcon(file)" size="lg" />
          </span>
          <div :class="classes.fileInfo">
            <div :class="classes.fileName">{{ file.name }}</div>
            <div :class="classes.fileMeta">Invalid file type</div>
          </div>
          <button type="button" :class="classes.fileRemove" aria-label="Remove file" @click="removeInvalidFile(file)">
            <e-icon name="close" size="sm" square />
          </button>
        </div>
      </e-primitive>
    </div>

    <!-- File list cards -->
    <div v-if="preview && fileList.length > 0" :class="classes.fileList">
      <e-primitive v-for="(file, i) in fileList" :key="i" :inner-class="fileCardCls">
        <div :class="classes.fileCard">
          <img v-if="getPreviewUrl(file)" :src="getPreviewUrl(file)!" :alt="file.name" :class="classes.filePreview" />
          <span v-else :class="classes.fileIcon">
            <e-icon :name="getFileIcon(file)" size="lg" />
          </span>
          <div :class="classes.fileInfo">
            <div :class="classes.fileName">{{ file.name }}</div>
            <div :class="classes.fileMeta">{{ formatFileSize(file.size) }}</div>
          </div>
          <button type="button" :class="classes.fileRemove" aria-label="Remove file" @click="removeFile(file)">
            <e-icon name="close" size="sm" square />
          </button>
        </div>
      </e-primitive>
    </div>
  </div>
</template>

<style>
/* display: block (not contents) — same as e-input, the host needs its own box for click area and sizing */
:host {
  display: block;
}
:host(:not([button])) {
  width: 100%;
}
</style>
